import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Clock, Linkedin, Facebook, Twitter, Share2 } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { blogPosts } from '../mock';

export default function BlogPost() {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;
  const related = blogPosts.filter(p => p.slug !== slug).slice(0, 3);

  return (
    <>
      <article className="pt-40 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          <Link to="/blog" className="inline-flex items-center text-sm text-slate-500 hover:text-amber-600 transition">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to blog
          </Link>
          <Badge className="mt-6 bg-amber-100 text-amber-800 hover:bg-amber-100">{post.category}</Badge>
          <h1 className="mt-4 text-4xl md:text-5xl font-bold text-[#0B1B33] tracking-tight leading-tight">{post.title}</h1>
          <div className="mt-5 flex items-center gap-5 text-sm text-slate-500">
            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> {post.date}</span>
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {post.readTime}</span>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 mt-12">
          <img src={post.image} alt={post.title} className="w-full h-[420px] object-cover rounded-3xl" />
        </div>

        <div className="max-w-3xl mx-auto px-6 mt-14">
          <div className="prose prose-slate max-w-none">
            <p className="text-xl text-slate-700 leading-relaxed font-medium">{post.excerpt}</p>
            {post.body.map((para, i) => (
              <p key={i} className="mt-6 text-slate-700 leading-relaxed text-[17px]">{para}</p>
            ))}
          </div>

          <div className="mt-12 pt-8 border-t border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Share2 className="w-4 h-4" /> Share this article
            </div>
            <div className="flex gap-2">
              {[Linkedin, Twitter, Facebook].map((Icon, i) => (
                <button key={i} className="w-10 h-10 rounded-full bg-slate-100 hover:bg-amber-500 hover:text-[#0B1B33] text-slate-600 flex items-center justify-center transition">
                  <Icon className="w-4 h-4" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </article>

      {/* Related */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-[#0B1B33] tracking-tight mb-10">Continue reading</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link to={`/blog/${p.slug}`} key={p.slug} className="group">
                <Card className="bg-white rounded-2xl overflow-hidden border-slate-100 h-full hover:shadow-xl transition">
                  <img src={p.image} alt={p.title} className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="p-5">
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-xs">{p.category}</Badge>
                    <h3 className="mt-3 font-bold text-[#0B1B33] group-hover:text-amber-600 transition leading-snug">{p.title}</h3>
                    <div className="mt-3 inline-flex items-center text-sm font-semibold text-[#0B1B33] group-hover:text-amber-600 transition">
                      Read <ArrowRight className="w-4 h-4 ml-1" />
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-[#0B1B33] rounded-3xl p-10 md:p-14 grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight leading-tight">Need an estimate, not just an article?</h2>
              <p className="mt-4 text-slate-300">Send your drawings — we’ll quote scope, lead time and a fixed fee in 4 business hours.</p>
            </div>
            <div className="lg:text-right">
              <Link to="/contact">
                <Button size="lg" className="bg-amber-500 hover:bg-amber-600 text-[#0B1B33] font-semibold h-12 px-7 rounded-xl">
                  Request a quote <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
